import _ from 'lodash-es';
import APIV5 from '~/models/api';
import QueryWhereRule from '~/scripts/services/model/query-where-rule';
import QueryWhereChild from '~/scripts/services/model/query-where-child';
import QueryWhereRules from '~/scripts/services/model/query-where-rules';
import QueryWhereChildren from '~/scripts/services/model/query-where-children';
import QueryWhere from '~/scripts/services/model/query-where';
import QueryWhereNode from '~/scripts/services/model/query-where-node';
// eslint-disable-next-line import/no-cycle
import { operatorsByType, types } from '../query-editor/query-editor.controller';

/**
 * Remonte les builders tant que leur nœud est `PATH_REL`. Ce marqueur désigne l'élément courant, de la classe
 * du nœud parent : ce n'est pas un K_NODE, KWS le rejette (`nodeid INVALID`) dans MF_Children2.
 *
 * @param {?QueryBuilderChildrenController} builder
 * @returns {?QueryBuilderChildrenController} - le builder portant la classe réelle
 */
function resolveClassBuilder(builder) {
	let current = builder;
	while (current?.group?.node instanceof QueryWhereNode && current.group.node.kid === 'PATH_REL') {
		current = current.parent;
	}
	return current;
}

export default class QueryBuilderChildrenController {
	/* @ngInject */
	/**
	 * @param {import('angular').IRootElementService} $element
	 * @param {import('angular').auto.IInjectorService} $injector
	 * @param {any} $scope
	 * @param {import('angular').ITimeoutService} $timeout
	 * @param {import('angular').ui.bootstrap.IPositionService} $uibPosition
	 * @param {any} KTranslate
	 */
	constructor(
		$element,
		$injector,
		$scope,
		$timeout,
		$uibPosition,
		KTranslate,
	) {
		this.$element = $element;
		this.$injector = $injector;
		this.$scope = $scope;
		this.$timeout = $timeout;
		this.$uibPosition = $uibPosition;
		this.KTranslate = KTranslate;

		this.conditions = [
			{ name: 'AND' },
			{ name: 'OR' },
		];

		/** @type {any} */
		this.formBuilder = undefined;

		/** @type {?QueryWhereChild} */
		this.group = null;

		/** @type {?import('../query-editor/query-editor.controller').default} */
		this.editor = null;

		/** @type {boolean} */
		this.loadingChildrenNodes = false;

		/** @type {any} */
		this.options = undefined;

		/** @type {?QueryBuilderChildrenController} */
		this.parent = null;

		this.root = undefined;
		this.tab = undefined;

		/** @type {{node: any}} */
		this.tmp = {
			node: undefined,
		};
	}

	/**
	 * @param {QueryWhereChild} group
	 * @param {QueryWhereChild} parent
	 */
	addChildren(group, parent) {
		const child = new QueryWhereChild({
			children: new QueryWhereChildren(),
			node: 'parent',
		}, parent);

		if (!group.children) {
			group.children = new QueryWhereChildren();
		}
		group.children?.items.push(child);
	}

	/**
	 * @param {QueryWhereChild} group
	 * @param {any | 'self'} item
	 * @param {string} type
	 */
	addNode(group, item, type) {
		if (!type) {
			throw new Error('missing type');
		}
		else if (type !== 'rule' && type !== 'child') {
			throw new Error(`invalid type ${type}`);
		}

		const node = item === 'self' ? 'self' : new QueryWhereNode(item);

		// Un groupe de classes n'a pas de nœud propre ('parent') : on hérite alors du nœud du builder qui le contient
		const parentNode = group.node instanceof QueryWhereNode ? group.node : this.group?.node;
		if (node !== 'self' && parentNode instanceof QueryWhereNode) {
			if (parentNode.inherits_extended === -1) {
				node.inherits_extended = -1;
			}
			if (parentNode.relative) {
				node.relative = true;
			}
		}

		if (type === 'rule') {
			const rule = new QueryWhereRule();
			if (node !== 'self') {
				rule.setNode(node);
			}
			// @ts-ignore
			rule.operator = operatorsByType[types[node.type]][1].id;

			// Object.assign(rule.node, node);
			if (!group.rules) {
				group.rules = new QueryWhereRules();
			}
			if (group.rules.items.length === 0) {
				group.rules.condition = 'AND';
			}
			group.rules.items.push(rule);
		}
		else if (type === 'child') {
			const child = new QueryWhereChild({ node });
			if (node === 'self') {
				child.rules = new QueryWhereRules();
				this.addRule(child.rules, group.node);
			}
			if (!group.children) {
				group.children = new QueryWhereChildren();
			}
			if (group.children.items.length === 0) {
				group.children.condition = 'AND';
			}
			group.children.items.push(child);
			this.tmp.node = undefined;
		}
	}

	/**
	 * @param {QueryWhereRules | QueryWhere | QueryWhereRule} rules
	 * @param {any} node
	 */
	addRule(rules, node) {
		const rule = new QueryWhereRule(null, node);
		const type = this.parent?.group?.node instanceof QueryWhereNode && node === 'self'
			? this.parent?.group?.node?.type
			: node.type;

		const operators = type ? operatorsByType[types[type]] : undefined;
		if (operators) {
			rule.operator = operators[1].id;
			if (rules instanceof QueryWhereRules && rules.items) {
				rules.items.push(rule);
			}
			else if (rules instanceof QueryWhereRule && rules.rules?.items) {
				rules.rules.items.push(rule);
			}
		}
	}

	/**
	 * @param {any} item
	 * @returns {any} result
	 */
	dropRule(item) {
		if (!this.group) {
			return;
		}

		// this.addRecursiveNode(this.group, item, 0);
		this.ensurePath(this.group, item);
	}

	enableRules() {
		if (!this.group) {
			return;
		}
		this.group.rules = new QueryWhereRules({
			condition: 'AND',
		});
		this.addRule(this.group.rules, this.group.node);
	}

	/**
	 * Parcourt le path de l'item niveau par niveau.
	 * Pour chaque niveau manquant dans le where tree, appelle addRule.
	 *
	 * @param {QueryWhereChild} child
	 * @param {any} item
	 */
	ensurePath(child, item) {
		let currentChild = child;
		let pathRelative = false;

		for (const [depth, step] of item.path.entries()) {
			const stepKid = step.kid ?? step.node ?? null;
			const stepLink = step.linker_kid ?? '0';

			if (stepKid === 'PATH_REL') {
				pathRelative = true;
			}

			step.relative = pathRelative;

			// Niveau 0 : on vérifie le nœud racine lui-même
			if (depth === 0 && currentChild.node instanceof QueryWhereNode && stepKid !== 'PATH_REL') {
				if (currentChild.node?.kid !== stepKid || (currentChild.node?.linker_kid ?? '0') !== stepLink) {
					console.error(`Le nœud racine ne correspond pas au path[0] (attendu: ${stepKid})`);
					return;
				}
				// Racine OK, on continue
				continue;
			}

			// Niveaux suivants : on cherche dans les children.items du nœud courant
			const items = currentChild.children?.items;
			/** @type {QueryWhereChild | false} */
			const existing = items?.find(
				child => child.node instanceof QueryWhereNode
					&& child.node?.kid === stepKid
					&& (child.node?.linker_kid ?? '0') === stepLink
			) ?? false;

			if (existing) {
				// Ce niveau existe déjà → on descend dedans
				currentChild = existing;
			}
			else {
				this.addNode(currentChild, step, 'child');

				// On suppose que addNode a poussé le nouveau nœud dans currentNode.children.items
				// On descend dans ce nouveau nœud pour les niveaux suivants
				const newNode = currentChild.children?.items.at(-1);
				if (!newNode) {
					console.error(`addNode n'a pas inséré de nœud pour le step: ${stepKid}`);
					return;
				}
				currentChild = newNode;
			}
		}
	}

	/**
	 * Trouve le nœud where dans lequel l'item doit être inséré.
	 * Retourne le nœud parent (depth = itemPath.length - 2),
	 * ou `where` lui-même si le path n'a qu'un seul élément.
	 *
	 * @param {QueryWhereChild} child  - nœud courant du where tree
	 * @param {any[]}  itemPath   - item.path
	 * @param {number} depth      - profondeur courante
	 * @returns {?QueryWhereChild}     - le nœud where dans lequel insérer, ou null si path invalide
	 */
	findInsertionNode(child, itemPath, depth = 0) {
		if (!child?.node || depth >= itemPath.length || !(child.node instanceof QueryWhereNode)) {
			return null;
		}

		const step = itemPath[depth];
		const stepKid = step.kid ?? step.node ?? null;
		const stepLink = step.linker_kid ?? '0';

		const nodeKid = child.node.kid;
		const nodeLink = child.node.linker_kid ?? '0';

		// Ce nœud ne correspond pas à cette étape du path
		if (nodeKid !== stepKid || nodeLink !== stepLink) {
			return null;
		}

		// On est au niveau du parent direct (avant-dernier step) → c'est ici qu'on insère
		if (depth === itemPath.length - 2) {
			return child;
		}

		// On est déjà à la feuille (path d'un seul élément) → insertion à la racine
		if (depth === itemPath.length - 1) {
			return child;
		}

		// On descend dans les enfants
		for (const c of child.children?.items ?? []) {
			const found = this.findInsertionNode(c, itemPath, depth + 1);
			if (found) {
				return found;
			}
		}

		return null;
	}

	async initNode() {
		const classBuilder = resolveClassBuilder(this);
		const classNode = classBuilder?.group?.node;
		if (this.group?.node instanceof QueryWhereNode && classNode instanceof QueryWhereNode && classNode.kid) {
			const parentNode = resolveClassBuilder(classBuilder?.parent ?? null)?.group?.node;
			this.loadingChildrenNodes = true;
			await this.group?.node?.init(APIV5.instance.mfChildren2(
				classNode.kid,
				'QUERY',
				{
					parentid: parentNode instanceof QueryWhereNode ? parentNode.kid : null,
					descendants: classNode.inherits_extended === 0,
				},
				{
					id: 'query-builder-children.init-node',
				},
			));
			this.$timeout(() => {
				this.loadingChildrenNodes = false;
			});
		}
	}

	removeChildren() {
		if (this.group?.children?.items && this.group.children.items.length > 0) {
			for (const group of this.group.children.items) {
				this.removeGroup(group, this.group)
			}
		}
	}

	/**
	 * @param {?QueryWhereChild} group
	 * @param {?QueryWhereChild} parent
	 */
	removeGroup(group, parent) {
		if (!group) {
			return;
		}
		group.children = null;
		group.node = null;
		if (parent?.children) {
			_.remove(parent.children.items, item => item.node === null);
			if (parent.children.items.length === 0) {
				parent.children = null;
			}
		}
	}

	/**
	 * @param {QueryWhereChild} group
	 */
	removeRules(group) {
		group.rules = null;
	}

	/**
	 * @param {any} item
	 */
	selectClassToProcess(item) {
		if (this.group?.node instanceof QueryWhereNode) {
			this.group.node.kid = item?.kid ?? 'K_NODE;ANYTHING';
			this.group.node.name = item?.name;
		}
	}

	$doCheck() {
		// automatically adds node if select.from is present
		if (
			!this.parent
			&& this.group
			&& (
				(this.group.node instanceof QueryWhereNode && !this.group.node?.kid)
				|| !this.group.node
			)
			&& (
				(this.editor?.tab && this.editor.tab.query.from?.length > 0)
				|| this.formBuilder?.query.from?.length > 0
			)
		) {
			const from = this.editor?.tab?.query.from ?? this.formBuilder?.query.from;
			this.group.node = from?.length > 0 ? new QueryWhereNode({
				flow:       null,
				kid:        from[0].node,
				linker_kid: '0', // TODO: check it
				name:       from[0].name,
				type:       from[0].type,
			}) : null;
		}
	}

	$onInit() {
		this.$scope.$watch('$ctrl.group', (/** @type {any} */ newValue, /** @type {any} */ oldValue) => {
			if (!_.isEqual(newValue, oldValue) && this.editor?.queryEditorForm) {
				this.editor.queryEditorForm.$setDirty();
			}

			this.tmp.node = this.group?.node instanceof QueryWhereNode
				? (this.group?.node?.kid === 'K_NODE;ANYTHING' ? undefined : this.group?.node?.kid)
				: undefined;
		}, true);
	}
}
